import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import AddIcon from '@mui/icons-material/AddOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { PERMISSIONS } from '@/types/auth';
import CategoryFormDialog from './CategoryFormDialog';
import type { CategoryFormValues } from './CategoryFormDialog';
import type { CreateMaterialCategoryRequest, MaterialCategoryDto, UpdateMaterialCategoryRequest } from '@/types/materials';

export default function CategoryMaster() {
  const { t } = useTranslation('categoryMaster');
  const queryClient = useQueryClient();

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['material-categories'],
    queryFn: async () => (await apiClient.get<MaterialCategoryDto[]>('/material-categories')).data,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<MaterialCategoryDto | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<MaterialCategoryDto | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['material-categories'] });
    // Every screen that filters materials by FG/SFG/RM (BOM Master, Material Request) reads
    // materialType off the cached /materials response — a category re-tag has to bust that too.
    queryClient.invalidateQueries({ queryKey: ['materials'] });
  };

  const saveMutation = useMutation({
    mutationFn: (values: CategoryFormValues) => {
      const type = values.type === '' ? null : values.type;
      if (editingCategory) {
        const payload: UpdateMaterialCategoryRequest = { name: values.name, parentCategoryId: values.parentCategoryId, status: values.status, type };
        return apiClient.put(`/material-categories/${editingCategory.id}`, payload);
      }
      const payload: CreateMaterialCategoryRequest = { name: values.name, parentCategoryId: values.parentCategoryId, type };
      return apiClient.post('/material-categories', payload);
    },
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      setEditingCategory(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/material-categories/${id}`),
    onSuccess: () => {
      invalidate();
      setConfirmDelete(null);
    },
  });

  const typeLabel = (type: string | null) => (type ? t(`form.type${type.charAt(0)}${type.slice(1).toLowerCase()}`) : t('form.typeNone'));

  const columns: DataTableColumn<MaterialCategoryDto>[] = [
    { field: 'name', headerName: t('table.name') },
    { field: 'parentCategoryName', headerName: t('table.parentCategory'), render: (r) => r.parentCategoryName ?? '—', filterable: true },
    { field: 'type', headerName: t('table.type'), render: (r) => typeLabel(r.type), searchValue: (r) => typeLabel(r.type), filterable: true },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      render: (r) => (
        <IfPermitted permission={PERMISSIONS.MATERIAL_MANAGE}>
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Button
              size="small"
              onClick={() => {
                setEditingCategory(r);
                setDialogOpen(true);
              }}
            >
              {t('edit')}
            </Button>
            <Button size="small" color="error" onClick={() => setConfirmDelete(r)}>
              {t('delete')}
            </Button>
          </Stack>
        </IfPermitted>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.MATERIAL_MANAGE}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingCategory(null);
                setDialogOpen(true);
              }}
            >
              {t('addCategory')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={categories} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="material-categories" />

      <CategoryFormDialog
        open={dialogOpen}
        category={editingCategory}
        categories={categories}
        onClose={() => {
          setDialogOpen(false);
          setEditingCategory(null);
        }}
        onSubmit={(values) => saveMutation.mutate(values)}
        loading={saveMutation.isPending}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        message={t('deleteCategory.message')}
        loading={deleteMutation.isPending}
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
        onClose={() => setConfirmDelete(null)}
      />
    </>
  );
}
